import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useCartStore } from '@/store/cartStore';
import type { Database } from '@/lib/database.types';
import { ImageWithFallback } from '@/components/ImageWithFallback';
import { useTranslation } from 'react-i18next';
import { Ingredients } from '@/components/product/Ingredients';

type Product = Database['public']['Tables']['products']['Row'] & {
  producers: Database['public']['Tables']['producers']['Row'];
  price_per_kg?: number | null;
  weight?: number | null;
  unit?: string | null;
  storage_location?: string | null;
  special_handling?: string | null;
};

export function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [product, setProduct] = React.useState<Product | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [selectedImage, setSelectedImage] = React.useState(0);
  const [hasIngredients, setHasIngredients] = React.useState(false);
  const addItem = useCartStore((state) => state.addItem);
  const [quantity, setQuantity] = React.useState(1);

  React.useEffect(() => {
    async function fetchProduct() {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          producers (*)
        `)
        .eq('slug', slug)
        .eq('is_published', true)
        .single();

      if (error) {
        console.error('Error fetching product:', error);
        navigate('/products');
        return;
      }

      setProduct(data);
      
      // Set page title
      if (data?.name) {
        document.title = `${data.name} - Lokala producenter`;
      }
      
      // Check if product has ingredients
      if (data?.id) {
        const { data: ingredientsData, error: ingredientsError } = await supabase
          .from('ingredients')
          .select('id')
          .eq('product_id', data.id)
          .limit(1);
          
        if (!ingredientsError && ingredientsData && ingredientsData.length > 0) {
          setHasIngredients(true);
        }
      }
      
      setLoading(false);
    }

    fetchProduct();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="lg:grid lg:grid-cols-2 lg:gap-x-8">
            <div className="h-96 bg-gray-200 rounded-lg" />
            <div className="mt-8 lg:mt-0 space-y-6">
              <div className="h-8 bg-gray-200 rounded w-3/4" />
              <div className="h-4 bg-gray-200 rounded w-1/2" />
              <div className="h-24 bg-gray-200 rounded" />
              <div className="h-12 bg-gray-200 rounded w-1/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center text-gray-500" data-testid="error-message">
          Produkten hittades inte
        </div>
      </div>
    );
  }

  // Main product image - use high quality for full size
  const mainImageSrc = product.image_urls[selectedImage] || 'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&q=80';

  return (
    <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      {/* Breadcrumb Navigation */}
      <nav className="flex mb-6" aria-label="Breadcrumb" data-testid="breadcrumb">
        <ol className="flex items-center space-x-2">
          <li>
            <Link to="/" className="text-gray-500 hover:text-gray-700">
              Hem
            </Link>
          </li>
          <li>
            <span className="text-gray-500 mx-2">/</span>
          </li>
          <li>
            <Link to="/products" className="text-gray-500 hover:text-gray-700">
              Produkter
            </Link>
          </li>
          <li>
            <span className="text-gray-500 mx-2">/</span>
          </li>
          <li>
            <span className="text-gray-900 font-medium">
              {product.name}
            </span>
          </li>
        </ol>
      </nav>
      
      <div className="lg:grid lg:grid-cols-2 lg:gap-x-8">
        <div className="space-y-4">
          <div className="rounded-lg overflow-hidden bg-gray-50" style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="main-product-image">
            <ImageWithFallback
              src={mainImageSrc}
              alt={product.name}
              className="max-w-full max-h-[400px] object-contain"
              priority={true}
              width={800}
              height={600}
              quality={90}
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
          </div>
          {product.image_urls.length > 1 && (
            <div className="grid grid-cols-4 gap-4" data-testid="image-thumbnails">
              {product.image_urls.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`relative rounded-lg overflow-hidden bg-gray-50 ${
                    selectedImage === index ? 'ring-2 ring-green-500' : ''
                  }`}
                  style={{ height: '80px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <ImageWithFallback
                    src={image}
                    alt={`${product.name} ${index + 1}`}
                    className="max-w-full max-h-[80px] object-contain"
                    width={120}
                    height={120}
                    quality={70}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 lg:mt-0">
          <h1 className="text-3xl font-extrabold text-gray-900" data-testid="product-heading">
            {product.name}
          </h1>
          
          <div className="mt-4">
            <p className="text-2xl text-gray-900" data-testid="product-price">{product.price.toLocaleString('sv-SE')} kr</p>
            {product.price_per_kg && (
              <p className="text-sm text-gray-600 mt-1" data-testid="product-unit">
                {product.price_per_kg.toLocaleString('sv-SE')} kr/kg
              </p>
            )}
          </div>
          
          {/* Product Weight/Unit Information */}
          {(product.weight || product.unit) && (
            <div className="mt-4">
              <p className="text-sm text-gray-600">
                {product.weight && `Vikt: ${product.weight} ${product.unit || ''}`}
              </p>
            </div>
          )}
          
          <div className="mt-6" data-testid="product-description">
            <h3 className="sr-only">Beskrivning</h3>
            <div 
              className="text-gray-700"
              dangerouslySetInnerHTML={{ __html: product.description || '' }}
            />
          </div>
          
          {/* Ingredients Section - only show if there are ingredients */}
          {hasIngredients && (
            <div className="mt-6">
              {product.id && <Ingredients productId={product.id} />}
            </div>
          )}
          
          <div className="mt-8" data-testid="add-to-cart-section">
            {/* Quantity Selector */}
            <div className="mb-4">
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
                Antal
              </label>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-md border border-gray-300 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:border-gray-400"
                  data-testid="decrease-quantity"
                  aria-label="Minska antal"
                >
                  -
                </button>
                <input
                  type="number"
                  id="quantity"
                  name="quantity"
                  min="1"
                  max="99"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-md text-center focus:ring-green-500 focus:border-green-500"
                  data-testid="quantity-input"
                />
                <button
                  type="button"
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 rounded-md border border-gray-300 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:border-gray-400"
                  data-testid="increase-quantity"
                  aria-label="Öka antal"
                >
                  +
                </button>
              </div>
            </div>
            
            <button
              onClick={() => {
                for (let i = 0; i < quantity; i++) {
                  addItem({
                    id: product.id,
                    name: product.name,
                    price: product.price,
                    producerId: product.producer_id
                  });
                }
              }}
              className="w-full bg-green-600 border border-transparent rounded-md py-3 px-8 flex items-center justify-center text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              data-testid="add-to-cart-button"
            >
              Lägg i kundvagn ({quantity} st)
            </button>
          </div>

          {/* Additional Product Details */}
          {(product.storage_location || product.special_handling) && (
            <div className="mt-6 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900">Produktdetaljer</h3>
              <dl className="mt-4 space-y-4">
                {product.storage_location && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Typ av vara</dt>
                    <dd className="mt-1 text-sm text-gray-900">{product.storage_location}</dd>
                  </div>
                )}
                {product.special_handling && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Särskilda hanteringsinstruktioner</dt>
                    <dd className="mt-1 text-sm text-gray-900">{product.special_handling}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          <div className="mt-10 border-t border-gray-200 pt-10" data-testid="producer-info">
            <h3 className="text-lg font-medium text-gray-900">Producent</h3>
            <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center">
              {product.producers.logo_url && (
                <div className="mb-4 sm:mb-0">
                  <ImageWithFallback
                    src={product.producers.logo_url}
                    alt={product.producers.company_name}
                    className="h-20 w-20 rounded-lg object-contain bg-white p-1 mr-4"
                    width={80}
                    height={80}
                    quality={80}
                  />
                </div>
              )}
              <div>
                <h4 className="text-sm font-medium text-gray-900" data-testid="producer-name">
                  {product.producers.company_name}
                </h4>
                <div 
                  className="mt-1 text-sm text-gray-500"
                  dangerouslySetInnerHTML={{ __html: product.producers.description || '' }}
                  data-testid="producer-description"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}